/**
 * Reports Service
 * Firestore operations for animal reports
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  type QueryConstraint,
  type DocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db, COLLECTIONS } from '../config/firebase'
import type { Report, ReportStatus, AnimalType, GeoLocation } from '../types'
import { encodeGeohash, getGeohashNeighbors, isWithinRadius } from '../utils/geo'

// ============================================================================
// Types
// ============================================================================

export interface ReportFilters {
  status?: ReportStatus | 'all'
  animalType?: AnimalType | 'all'
  country?: string
  city?: string
  dateRange?: 'today' | 'week' | 'month' | 'all'
}

export interface PaginationOptions {
  pageSize?: number
  lastDoc?: DocumentSnapshot
}

export interface CreateReportData {
  animalType: AnimalType
  description?: string
  location: GeoLocation
  locationDetails?: {
    displayName?: string
    city?: string
    country?: string
    state?: string
  }
  manualLocation?: string
  photoURL?: string
  photoURLs?: string[]
  consent?: boolean
}

// ============================================================================
// Collection Reference
// ============================================================================

const reportsCollection = collection(db, COLLECTIONS.REPORTS)

// ============================================================================
// Read Operations
// ============================================================================

/**
 * Get a single report by ID
 */
export async function getReport(id: string): Promise<Report | null> {
  const docRef = doc(db, COLLECTIONS.REPORTS, id)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) {
    return null
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Report
}

/**
 * Get all reports with optional filters and pagination
 */
export async function getReports(
  filters: ReportFilters = {},
  pagination: PaginationOptions = {}
): Promise<{ reports: Report[]; lastDoc: DocumentSnapshot | null }> {
  const constraints: QueryConstraint[] = []

  // Apply status filter
  if (filters.status && filters.status !== 'all') {
    constraints.push(where('status', '==', filters.status))
  }

  // Apply animal type filter
  if (filters.animalType && filters.animalType !== 'all') {
    constraints.push(where('animalType', '==', filters.animalType))
  }

  // Apply country filter
  if (filters.country && filters.country !== 'all') {
    constraints.push(where('country', '==', filters.country))
  }

  // Apply city filter
  if (filters.city && filters.city !== 'all') {
    constraints.push(where('city', '==', filters.city))
  }

  // Apply date range filter
  if (filters.dateRange && filters.dateRange !== 'all') {
    const now = new Date()
    let startDate: Date

    switch (filters.dateRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      default:
        startDate = new Date(0)
    }

    constraints.push(where('timestamp', '>=', startDate))
  }

  // Order by timestamp descending
  constraints.push(orderBy('timestamp', 'desc'))

  // Apply pagination
  const pageSize = pagination.pageSize ?? 20
  constraints.push(limit(pageSize))

  if (pagination.lastDoc) {
    constraints.push(startAfter(pagination.lastDoc))
  }

  const q = query(reportsCollection, ...constraints)
  const snapshot = await getDocs(q)

  const reports: Report[] = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Report[]

  const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null

  return { reports, lastDoc }
}

/**
 * Get reports near a location using geohash
 */
export async function getReportsNearLocation(
  center: GeoLocation,
  radiusKm: number,
  filters: ReportFilters = {}
): Promise<Report[]> {
  const geohash = encodeGeohash(center.latitude, center.longitude, 5)
  const neighbors = getGeohashNeighbors(geohash)

  // Query all neighboring geohash cells
  const allReports: Report[] = []

  for (const hash of neighbors) {
    const constraints: QueryConstraint[] = [
      where('geohash', '>=', hash),
      where('geohash', '<=', hash + '\uf8ff'),
    ]

    // Apply status filter for approved reports only by default
    if (filters.status && filters.status !== 'all') {
      constraints.push(where('status', '==', filters.status))
    }

    const q = query(reportsCollection, ...constraints)
    const snapshot = await getDocs(q)

    snapshot.docs.forEach((doc) => {
      const report = { id: doc.id, ...doc.data() } as Report

      // Filter by exact radius using Haversine formula
      if (isWithinRadius(center, report.location, radiusKm)) {
        allReports.push(report)
      }
    })
  }

  // Remove duplicates (reports might appear in multiple geohash cells)
  const uniqueReports = Array.from(new Map(allReports.map((r) => [r.id, r])).values())

  // Sort by distance from center
  return uniqueReports.sort((a, b) => {
    if (!a.location || !b.location) {
      return 0
    }
    const distA = Math.hypot(
      a.location.latitude - center.latitude,
      a.location.longitude - center.longitude
    )
    const distB = Math.hypot(
      b.location.latitude - center.latitude,
      b.location.longitude - center.longitude
    )
    return distA - distB
  })
}

/**
 * Get pending reports (for admin approval)
 */
export async function getPendingReports(): Promise<Report[]> {
  const q = query(reportsCollection, where('status', '==', 'pending'), orderBy('timestamp', 'desc'))

  const snapshot = await getDocs(q)

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Report[]
}

/**
 * Subscribe to real-time report updates
 */
export function subscribeToReports(
  callback: (reports: Report[]) => void,
  filters: ReportFilters = {}
): Unsubscribe {
  const constraints: QueryConstraint[] = []

  if (filters.status && filters.status !== 'all') {
    constraints.push(where('status', '==', filters.status))
  }

  constraints.push(orderBy('timestamp', 'desc'))
  constraints.push(limit(100))

  const q = query(reportsCollection, ...constraints)

  return onSnapshot(q, (snapshot) => {
    const reports = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Report[]

    callback(reports)
  })
}

// ============================================================================
// Write Operations
// ============================================================================

/**
 * Create a new report
 */
export async function createReport(data: CreateReportData): Promise<string> {
  // Runtime validation - defense in depth (cast to unknown to bypass compile-time guarantees)
  const loc = data.location as unknown as Record<string, unknown> | null | undefined
  if (
    !loc ||
    typeof loc.latitude !== 'number' ||
    typeof loc.longitude !== 'number' ||
    isNaN(loc.latitude) ||
    isNaN(loc.longitude)
  ) {
    throw new Error('Valid GPS location is required to submit a report')
  }

  const geohash = encodeGeohash(data.location.latitude, data.location.longitude, 9)

  const reportData = {
    ...data,
    geohash,
    status: 'pending' as ReportStatus,
    timestamp: serverTimestamp(),
    caseId: generateCaseId(),
  }

  const docRef = await addDoc(reportsCollection, reportData)
  return docRef.id
}

/**
 * Update report status (admin action)
 */
export async function updateReportStatus(
  id: string,
  status: ReportStatus,
  notes?: string
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.REPORTS, id)

  const updateData = {
    status,
    updatedAt: serverTimestamp(),
    ...(notes ? { adminNotes: notes } : {}),
    ...(status === 'new' ? { approvedAt: serverTimestamp() } : {}),
  }

  await updateDoc(docRef, updateData)
}

/**
 * Update report details
 */
export async function updateReport(id: string, data: Partial<Report>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.REPORTS, id)

  // If location is updated, recalculate geohash
  const updateData = {
    ...data,
    updatedAt: serverTimestamp(),
    ...(data.location
      ? { geohash: encodeGeohash(data.location.latitude, data.location.longitude, 9) }
      : {}),
  }

  await updateDoc(docRef, updateData)
}

/**
 * Delete a report
 */
export async function deleteReport(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.REPORTS, id)
  await deleteDoc(docRef)
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a unique case ID
 * Format: POM-[timestamp]-[random]
 */
function generateCaseId(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 6).toUpperCase()
  return `POM-${timestamp}-${random}`
}

/**
 * Get unique countries from reports
 */
export async function getUniqueCountries(): Promise<string[]> {
  const snapshot = await getDocs(reportsCollection)
  const countries = new Set<string>()

  snapshot.docs.forEach((doc) => {
    const data = doc.data()
    if (data.country) {
      countries.add(data.country as string)
    }
  })

  return Array.from(countries).sort()
}

/**
 * Get unique cities for a country
 */
export async function getCitiesForCountry(country: string): Promise<string[]> {
  const q = query(reportsCollection, where('country', '==', country))
  const snapshot = await getDocs(q)
  const cities = new Set<string>()

  snapshot.docs.forEach((doc) => {
    const data = doc.data()
    if (data.city) {
      cities.add(data.city as string)
    }
  })

  return Array.from(cities).sort()
}

/**
 * Get report statistics
 */
export async function getReportStats(): Promise<{
  total: number
  pending: number
  new: number
  investigating: number
  resolved: number
  closed: number
  byAnimalType: Record<AnimalType, number>
}> {
  const snapshot = await getDocs(reportsCollection)

  const stats = {
    total: 0,
    pending: 0,
    new: 0,
    investigating: 0,
    resolved: 0,
    closed: 0,
    byAnimalType: {
      dog: 0,
      cat: 0,
      bird: 0,
      other: 0,
    } as Record<AnimalType, number>,
  }

  snapshot.docs.forEach((doc) => {
    const data = doc.data()
    stats.total++

    // Count by status
    const status = data.status as ReportStatus
    if (status in stats) {
      stats[status as keyof typeof stats]++
    }

    // Count by animal type
    const animalType = data.animalType as AnimalType
    if (animalType in stats.byAnimalType) {
      stats.byAnimalType[animalType]++
    }
  })

  return stats
}
